import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trade } from '@/models/trade';
import { useCrmUpdateTrade } from '@/api/trades/use-trades';
import { useCrmDeleteTrade } from '@/api/trades/use-crm-trades';
import { InlineEdit } from '@/components/ui/inline-edit';
import { useSnackbar } from 'notistack';
import { Trash2, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/lib/format';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ModulePermission } from '@/types/permissions.ts';
import { formatInstrumentPrice, formatAmount } from '@/utils/formatters';

interface TradeDetailsModalProps {
    trade: Trade | null;
    isOpen: boolean;
    onClose: () => void;
    onTradeUpdated?: (updatedTrade: Trade) => void;
    onTradeDeleted?: () => void;
}

const TradeDetailsModal: React.FC<TradeDetailsModalProps> = ({
    trade,
    isOpen,
    onClose,
    onTradeUpdated,
    onTradeDeleted,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const updateTradeMutation = useCrmUpdateTrade();
    const deleteTradeMutation = useCrmDeleteTrade();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const handleFieldUpdate = async (field: keyof Trade, value: any) => {
        if (!trade) return;

        try {
            const result = await updateTradeMutation.mutateAsync({
                id: trade.id,
                data: { [field]: value },
            });

            enqueueSnackbar('Trade updated successfully', { variant: 'success' });

            // Call the callback to update the parent component's trade data
            if (onTradeUpdated && result.data) {
                onTradeUpdated(result.data);
            }
        } catch (error) {
            enqueueSnackbar('Failed to update trade', { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!trade) return;

        try {
            await deleteTradeMutation.mutateAsync(trade.id);
            setShowDeleteDialog(false);
            onClose();
            if (onTradeDeleted) {
                onTradeDeleted();
            }
        } catch (error) {
            enqueueSnackbar('Failed to delete trade', { variant: 'error' });
        }
    };

    if (!trade) return null;

    const getTypeClassName = (type: string) => {
        switch (type.toLowerCase()) {
            case 'buy':
                return 'bg-success/10 text-success border-success/20';
            case 'sell':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            default:
                return '';
        }
    };

    const formatTradeDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return formatDate(dateString, 'datetime');
    };

    const formatDateForInput = (dateString?: string) => {
        if (!dateString) return '';
        // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            Trade Details
                            <Badge variant="outline" className={getTypeClassName(trade.side)}>
                                {trade.side.toUpperCase()}
                            </Badge>
                            {trade.instrument && (
                                <span className="text-sm font-normal text-muted-foreground">
                                    {trade.instrument.symbol}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                        <div className="space-y-4">
                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Trade Information</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">ID:</span>
                                        <span className="text-sm font-medium">{trade.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Status:
                                        </span>
                                        <InlineEdit
                                            type="select"
                                            value={trade.status || 'open'}
                                            onSave={value => handleFieldUpdate('status', value)}
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Select status"
                                            selectOptions={[
                                                { value: 'pending', label: 'Pending' },
                                                { value: 'open', label: 'Open' },
                                                { value: 'closed', label: 'Closed' },
                                            ]}
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Created At:
                                        </span>
                                        <InlineEdit
                                            type="text"
                                            value={
                                                trade.created_at
                                                    ? formatDateForInput(trade.created_at)
                                                    : ''
                                            }
                                            onSave={value => {
                                                // Parse datetime-local format (YYYY-MM-DDTHH:mm) to ISO string
                                                if (value) {
                                                    const dateValue = new Date(
                                                        value.toString()
                                                    ).toISOString();
                                                    handleFieldUpdate('created_at', dateValue);
                                                }
                                            }}
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="YYYY-MM-DDTHH:mm"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Opened At:
                                        </span>
                                        <InlineEdit
                                            type="text"
                                            value={
                                                trade.opened_at
                                                    ? formatDateForInput(trade.opened_at)
                                                    : ''
                                            }
                                            onSave={value => {
                                                // Parse datetime-local format (YYYY-MM-DDTHH:mm) to ISO string
                                                if (value) {
                                                    const dateValue = new Date(
                                                        value.toString()
                                                    ).toISOString();
                                                    handleFieldUpdate('opened_at', dateValue);
                                                } else {
                                                    handleFieldUpdate('opened_at', null);
                                                }
                                            }}
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="YYYY-MM-DDTHH:mm"
                                        />
                                    </div>
                                    {trade.closed_at && (
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Closed At:
                                            </span>
                                            <span className="text-sm font-medium">
                                                {formatTradeDate(trade.closed_at)}
                                            </span>
                                        </div>
                                    )}
                                    {trade.duration_minutes !== undefined &&
                                        trade.duration_minutes !== null && (
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">
                                                    Duration:
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {Math.floor(trade.duration_minutes / 60)}h{' '}
                                                    {trade.duration_minutes % 60}m
                                                </span>
                                            </div>
                                        )}
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Close Reason:
                                        </span>
                                        <InlineEdit
                                            type="select"
                                            value={trade.close_reason || ''}
                                            onSave={value =>
                                                handleFieldUpdate('close_reason', value || null)
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Select close reason"
                                            selectOptions={[
                                                { value: 'manual', label: 'Manual' },
                                                { value: 'stop_loss', label: 'Stop Loss' },
                                                { value: 'take_profit', label: 'Take Profit' },
                                                { value: 'liquidation', label: 'Liquidation' },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Instrument Details</h3>
                                <div className="space-y-2">
                                    {trade.instrument && (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">
                                                    Symbol:
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {trade.instrument.symbol}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-muted-foreground">
                                                    Name:
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {trade.instrument.name || 'N/A'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Instrument ID:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {trade.instrument_id}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Trade Details</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Side:</span>
                                        <Badge
                                            variant="outline"
                                            className={getTypeClassName(trade.side)}
                                        >
                                            {trade.side.toUpperCase()}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Lot Size:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.lot_size
                                                    ? formatAmount(Number(trade.lot_size), 5)
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'lot_size',
                                                    parseFloat(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter lot size"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Entry Price:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.entry_price
                                                    ? formatInstrumentPrice(
                                                          Number(trade.entry_price),
                                                          trade.instrument as any
                                                      )
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'entry_price',
                                                    parseFloat(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter entry price"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Exit Price:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.exit_price
                                                    ? formatInstrumentPrice(
                                                          Number(trade.exit_price),
                                                          trade.instrument as any
                                                      )
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'exit_price',
                                                    value ? parseFloat(value.toString()) : null
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter exit price"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Current Price:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {trade.current_price
                                                ? formatInstrumentPrice(
                                                      Number(trade.current_price),
                                                      trade.instrument as any
                                                  )
                                                : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Stop Price:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.stop_price
                                                    ? formatInstrumentPrice(
                                                          Number(trade.stop_price),
                                                          trade.instrument as any
                                                      )
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'stop_price',
                                                    value ? parseFloat(value.toString()) : null
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter stop price"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Take Profit:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.take_profit_price
                                                    ? formatInstrumentPrice(
                                                          Number(trade.take_profit_price),
                                                          trade.instrument as any
                                                      )
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'take_profit_price',
                                                    value ? parseFloat(value.toString()) : null
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter take profit"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Stop Loss:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.stop_loss_price
                                                    ? formatInstrumentPrice(
                                                          Number(trade.stop_loss_price),
                                                          trade.instrument as any
                                                      )
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'stop_loss_price',
                                                    value ? parseFloat(value.toString()) : null
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter stop loss"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Leverage:
                                        </span>
                                        <InlineEdit
                                            value={trade.leverage?.toString() || ''}
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'leverage',
                                                    parseInt(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter leverage"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Margin Used:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.margin_used
                                                    ? formatAmount(Number(trade.margin_used), 5)
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'margin_used',
                                                    parseFloat(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter margin used"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Performance</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">PnL:</span>
                                        <span
                                            className={`text-sm font-medium ${
                                                (trade.realized_pnl || trade.unrealized_pnl || 0) >
                                                0
                                                    ? 'text-success'
                                                    : (trade.realized_pnl ||
                                                            trade.unrealized_pnl ||
                                                            0) < 0
                                                      ? 'text-destructive'
                                                      : ''
                                            }`}
                                        >
                                            {formatAmount(
                                                trade.status === 'closed'
                                                    ? trade.realized_pnl || 0
                                                    : trade.unrealized_pnl || 0,
                                                5
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            PnL (Base Currency):
                                        </span>
                                        <span
                                            className={`text-sm font-medium ${
                                                (trade.realized_pnl_base ||
                                                    trade.unrealized_pnl_base ||
                                                    0) > 0
                                                    ? 'text-success'
                                                    : (trade.realized_pnl_base ||
                                                            trade.unrealized_pnl_base ||
                                                            0) < 0
                                                      ? 'text-destructive'
                                                      : ''
                                            }`}
                                        >
                                            {formatAmount(
                                                trade.status === 'closed'
                                                    ? trade.realized_pnl_base || 0
                                                    : trade.unrealized_pnl_base || 0,
                                                5
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Commission:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.commission
                                                    ? formatAmount(Number(trade.commission), 5)
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'commission',
                                                    parseFloat(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter commission"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Swap Fees:
                                        </span>
                                        <InlineEdit
                                            value={
                                                trade.swap_fees
                                                    ? formatAmount(Number(trade.swap_fees), 5)
                                                    : ''
                                            }
                                            onSave={value =>
                                                handleFieldUpdate(
                                                    'swap_fees',
                                                    parseFloat(value.toString())
                                                )
                                            }
                                            permissionModule={ModulePermission.TRADES}
                                            placeholder="Enter swap fees"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Max Drawdown:
                                        </span>
                                        <span className="text-sm font-medium text-destructive">
                                            {formatAmount(Number(trade.max_drawdown || 0), 5)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Max Profit:
                                        </span>
                                        <span className="text-sm font-medium text-success">
                                            {formatAmount(Number(trade.max_profit || 0), 5)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {trade.metadata && (
                            <div className="col-span-1 md:col-span-2 bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Additional Information</h3>
                                <div className="space-y-2">
                                    {(() => {
                                        // Parse metadata if it's a string
                                        let metadataObj = trade.metadata;
                                        if (typeof metadataObj === 'string') {
                                            try {
                                                metadataObj = JSON.parse(metadataObj);
                                            } catch (e) {
                                                // If parsing fails, display as is
                                                return (
                                                    <div className="flex justify-between">
                                                        <span className="text-sm text-muted-foreground">
                                                            Raw Data:
                                                        </span>
                                                        <span className="text-sm font-medium">
                                                            {metadataObj}
                                                        </span>
                                                    </div>
                                                );
                                            }
                                        }

                                        // If it's not an object or is null after parsing, display as is
                                        if (
                                            !metadataObj ||
                                            typeof metadataObj !== 'object' ||
                                            Array.isArray(metadataObj)
                                        ) {
                                            return (
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-muted-foreground">
                                                        Value:
                                                    </span>
                                                    <span className="text-sm font-medium">
                                                        {String(metadataObj)}
                                                    </span>
                                                </div>
                                            );
                                        }

                                        // Display as key-value pairs
                                        return Object.entries(metadataObj).map(([key, value]) => (
                                            <div key={key} className="flex justify-between">
                                                <span className="text-sm text-muted-foreground capitalize">
                                                    {key.replace(/_/g, ' ')}:
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {typeof value === 'object'
                                                        ? JSON.stringify(value)
                                                        : String(value)}
                                                </span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="destructive"
                            onClick={() => setShowDeleteDialog(true)}
                            className="gap-2"
                            disabled={deleteTradeMutation.isPending}
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete Trade
                        </Button>
                        <Button variant="outline" onClick={onClose}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Delete Trade
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this trade? This action cannot be
                            undone.
                            <br />
                            <br />
                            <strong>Trade ID:</strong> {trade.id}
                            <br />
                            <strong>Instrument:</strong> {trade.instrument?.symbol || 'N/A'}
                            <br />
                            <strong>Side:</strong> {trade.side?.toUpperCase() || 'N/A'}
                            <br />
                            <strong>Lot Size:</strong>{' '}
                            {trade.lot_size ? formatAmount(Number(trade.lot_size), 5) : 'N/A'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteTradeMutation.isPending}
                        >
                            {deleteTradeMutation.isPending ? 'Deleting...' : 'Delete Trade'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default TradeDetailsModal;
